import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import {
  BENI_COMPARISON_SETS,
  type ComparisonSet,
} from 'frontend-utils/utils/constants/benchmark-insight';
import HotelBenchmarkInsightTooltipsSyncStatusBeniDataSyncStatusTooltip from 'lumen/components/hotel/benchmark-insight/tooltips/sync-status/beni-data-sync-status-tooltip';
import type { BeniMetaComparisonSet } from 'lumen/models';
import type { DataSyncStatusItem } from 'lumen/utils/constants/benchmark-insight/beni-data-sync-constant-values';

interface HotelBenchmarkInsightDataSyncStatusSignature {
  Args: {
    comparisonSets: BeniMetaComparisonSet[];
    extractDate: string;
    extractDateDebugging?: string;
  };
}

export default class HotelBenchmarkInsightDataSyncStatus extends Component<HotelBenchmarkInsightDataSyncStatusSignature> {
  @service('benchmark-insight/beni-data-sync-service')
  declare benchmarkInsightBeniDataSyncService: Services['benchmark-insight/beni-data-sync-service'];
  @service('benchmark-insight/beni-service')
  declare benchmarkInsightBeniService: Services['benchmark-insight/beni-service'];
  @service declare intl: Services['intl'];

  get allAvailableDataSetsInSync(): boolean {
    // Don't consider unavailable extract dates (`extract_Date` is `null`) for the header text
    return this.syncData.every(({ extractDate, isSynced }) =>
      extractDate ? isSynced : true,
    );
  }

  get dataExtractDates(): (string | null)[] {
    return this.syncData.map(({ extractDate }) => extractDate);
  }

  get headerText(): string {
    const { extractDateDebugging } = this.args;

    if (extractDateDebugging) {
      const formattedExtractDateDebugging =
        dayjs(extractDateDebugging).format('D MMMM YYYY');

      return `Debugging extract date: ${formattedExtractDateDebugging}`;
    }

    const result = this.allAvailableDataSetsInSync
      ? this.intl.t(
          'benchmark_insight.data_sync_status.trigger-text.synchronised',
        )
      : this.intl.t(
          'benchmark_insight.data_sync_status.trigger-text.out-of-sync',
          {
            count:
              this.benchmarkInsightBeniDataSyncService.oldestExtractDateDayDiff(
                this.dataExtractDates,
              ),
          },
        );
    return result;
  }

  get syncData(): DataSyncStatusItem[] {
    let comparisonSets = this.args.comparisonSets?.filter(
      ({ exists, comparisonSetType }) =>
        comparisonSetType === BENI_COMPARISON_SETS.OWN ||
        (exists &&
          this.benchmarkInsightBeniService.allowedComparisonSets.includes(
            comparisonSetType,
          )),
    );

    if (!comparisonSets || comparisonSets.length === 0) {
      return [];
    }
    const hasBothMarketClasses = this.hasBothMarketClasses(
      comparisonSets?.map(({ comparisonSetType }) => comparisonSetType),
    );

    if (hasBothMarketClasses) {
      comparisonSets =
        this.benchmarkInsightBeniDataSyncService.filterMarketClass(
          comparisonSets,
        );
    }
    return this.benchmarkInsightBeniDataSyncService.mapComparisonSetsToDataSyncList(
      comparisonSets,
    );
  }

  hasBothMarketClasses(comparisonSets: ComparisonSet[]): boolean {
    return (
      comparisonSets.includes(BENI_COMPARISON_SETS.MARKET_CLASS) &&
      comparisonSets.includes(BENI_COMPARISON_SETS.MARKET_COLLAPSED_CLASS)
    );
  }

  <template>
    <HotelBenchmarkInsightTooltipsSyncStatusBeniDataSyncStatusTooltip
      @allDataSetsInSync={{this.allAvailableDataSetsInSync}}
      @frequencyMessage={{t
        "benchmark_insight.data_sync_status.refresh_frequency_message"
      }}
      @headerText={{this.headerText}}
      @items={{this.syncData}}
    />
  </template>
}
